import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useMediaQuery } from "../../../lib/hooks";
import {
    IconPhoto,
    IconFileDescription,
    IconTag,
    IconX,
    IconPhotoEdit,
    IconStack2,
    IconSparkles,
    IconStar,
    IconShieldCheck,
    IconCoins,
    IconDiamond,
    IconInfoCircle,
    IconPrinter,
    IconCheck,
    IconGift,
    IconUsers,
    IconMapPin,
    IconClipboardList,
    IconFileExport,
    IconForms,
    IconTools,
} from "@tabler/icons-react";
import { notifications } from "../../../lib/notifications";
import { withFakeBuyers, currentSlotIndex } from "../../../config/socialProofBuyers";
import paymentService from "../../../services/paymentService";
import usageService from "../../../services/usageService";
import planService from "../../../services/planService";
import { useAuthStore } from "../../../store/authStore";
import {
    WALLET_CREDIT_COSTS,
    calculateActionsBreakdown,
} from "../../../config/creditPackages";
import { creditParts } from "../../../utils/credit";
import VideoHelpButton from "../../../components/common/VideoHelpButton";
import { PAYMENT_GUIDE_VIDEO_URL } from "../../../config/helpVideos";

// Reusable skeleton block class (shimmer) used by the loading placeholder.
const SK = "animate-pulse bg-zinc-200/80 dark:bg-zinc-800 rounded";

// ── SSLCommerz EasyCheckout (official popup) ─────────────────────────────────
// Set false to skip the popup and use a plain full-page redirect (always works).
const SSLCZ_USE_POPUP = true;
// Match the backend's SSLCZ_IS_LIVE: live loads embed.min.js from seamless-epay, sandbox
// from the sandbox host.
const SSLCZ_LIVE = true;
const SSLCZ_EMBED_SRC = SSLCZ_LIVE
    ? "https://seamless-epay.sslcommerz.com/embed.min.js"
    : "https://sandbox.sslcommerz.com/embed.min.js";
// Same base-URL rule as services/api.js so the widget's own XHR reaches the backend.
const API_BASE = import.meta.env.VITE_API_URL || "";

// CRITICAL: embed.min.js reads the #sslczPayBtn's `order`/`endpoint` attributes ONCE, when the
// script first runs, and caches them in a closure for the life of that script load. Its click
// handler then always POSTs that first-captured order. So if we just update the attributes and
// re-click for a different package, the widget re-sends the OLD order → the popup shows the old
// amount. To open a *different* package we must fully re-initialize the widget: swap in a fresh
// button (so the previous load's handler, bound to the old node, can't fire) and re-inject the
// script so it re-reads the current attributes. See openSslczPopup below.
function loadSslczEmbed() {
    return new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.dataset.sslcz = "1"; // tag so we can drop it before the next re-init
        s.src = SSLCZ_EMBED_SRC + "?" + Math.random().toString(36).substring(7);
        // The widget binds a beat after the script's load event — resolve slightly later so
        // the click isn't fired before the handler is attached.
        s.onload = () => setTimeout(() => resolve(true), 700);
        s.onerror = () => reject(new Error("embed load failed"));
        document.body.appendChild(s);
    });
}

// Tear down artifacts a previous open left in the DOM: the old embed script, its loading
// overlay (div.shaz_back_overlay), and any hidden modal (.tingle-modal). Then replace
// #sslczPayBtn with a fresh clone so the prior script's click handler (bound to the old node)
// is gone. Returns the fresh button, or null if the host button isn't present.
function resetSslczWidget() {
    document.querySelectorAll('script[data-sslcz="1"], .shaz_back_overlay, .tingle-modal')
        .forEach((el) => el.remove());
    const old = document.getElementById("sslczPayBtn");
    if (!old) return null;
    const fresh = old.cloneNode(false);
    fresh.removeAttribute("order");
    fresh.removeAttribute("endpoint");
    old.replaceWith(fresh);
    return fresh;
}

// The popup is a cross-origin iframe. A cross-origin-isolated page (the Vite dev server sends
// COOP/COEP for ffmpeg + background removal) refuses to render one unless it is `credentialless`,
// which is evaluated when the iframe is CREATED — and embed.min.js creates it without. So when
// the page is isolated, swap the widget's iframe for an identical credentialless one the moment
// it appears. Not isolated (production) → untouched.
const SSLCZ_CAN_POPUP = typeof window === "undefined"
    || !window.crossOriginIsolated
    || "credentialless" in HTMLIFrameElement.prototype;
function watchSslczIframe() {
    if (!window.crossOriginIsolated || !("credentialless" in HTMLIFrameElement.prototype)) return;
    const mo = new MutationObserver((muts) => {
        for (const m of muts) for (const n of m.addedNodes) {
            const old = n.nodeType === 1 && (n.matches?.("iframe") ? n : n.querySelector?.("iframe"));
            if (!old || old.hasAttribute("credentialless")) continue;
            const src = old.getAttribute("src");
            const fresh = document.createElement("iframe");
            for (const a of old.attributes) if (a.name !== "src") fresh.setAttribute(a.name, a.value);
            fresh.setAttribute("credentialless", "");
            old.replaceWith(fresh);
            fresh.src = src;
            mo.disconnect();
            return;
        }
    });
    mo.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => mo.disconnect(), 20000);
}

// Open the official SSLCommerz popup for an already-created session. Returns true if the
// popup was triggered, false if it couldn't (caller then falls back to a full redirect).
//
// Order matters (verified empirically): the #sslczPayBtn must exist AND have its attributes
// set BEFORE embed.min.js is injected, because the widget binds to the button — with its
// attributes — as it initializes. So: reset → set attrs → load embed → click. The reset makes
// this correct for the 2nd, 3rd, … open too (fresh capture each time), which fixes the popup
// showing a previously-selected package's amount after switching packages.
async function openSslczPopup(orderId) {
    const btn = resetSslczWidget();
    if (!btn) return false;
    // The widget reads these: `order` (required, non-empty) and `endpoint` (its XHR target,
    // which returns { status:"success", data:GatewayPageURL, logo } from our backend).
    btn.setAttribute("order", orderId);
    btn.setAttribute("endpoint", `${API_BASE}/api/payments/ssl/embed-open?orderId=${encodeURIComponent(orderId)}`);
    try {
        await loadSslczEmbed(); // inject AFTER the fresh button+attrs exist so the widget binds to it
    } catch {
        return false; // script blocked/offline → redirect instead
    }
    watchSslczIframe();
    btn.click();
    return true;
}

// ── FastSpring (international / USD) popup checkout ──────────────────────────
// The Store Builder Library (fbc.js) is loaded once with our popup storefront and two
// global callbacks. A server-created session is opened with fastspring.builder.checkout(id);
// settlement happens via our backend webhook, so after the popup closes / signals a webhook
// we poll /payments/status to confirm and refresh the wallet balance.
const FASTSPRING_STOREFRONT = import.meta.env.VITE_FASTSPRING_STOREFRONT || "";
const FASTSPRING_SBL_SRC = "https://sbl.onfastspring.com/sbl/1.0.7/fastspring-builder.min.js";

let fastspringSblPromise = null;
function loadFastSpringSbl() {
    if (fastspringSblPromise) return fastspringSblPromise;
    fastspringSblPromise = new Promise((resolve, reject) => {
        if (window.fastspring?.builder) return resolve(true);
        const s = document.createElement("script");
        s.id = "fsc-api";
        s.type = "text/javascript";
        s.src = FASTSPRING_SBL_SRC;
        s.setAttribute("data-storefront", FASTSPRING_STOREFRONT);
        // SBL invokes these globals by name; the component (re)assigns them per checkout.
        s.setAttribute("data-popup-closed", "__fsOnPopupClosed");
        s.setAttribute("data-popup-webhook-received", "__fsOnPopupWebhook");
        s.onload = () => resolve(true);
        s.onerror = () => reject(new Error("FastSpring SBL load failed"));
        document.head.appendChild(s);
    });
    return fastspringSblPromise;
}

// Open the FastSpring popup for an already-created session. Returns true if triggered.
async function openFastSpringPopup(sessionId) {
    if (!FASTSPRING_STOREFRONT) return false;
    try { await loadFastSpringSbl(); } catch { return false; }
    if (!window.fastspring?.builder?.checkout) return false;
    window.fastspring.builder.checkout(sessionId);
    return true;
}

// `gateway` selects the payment backend: 'moneybag' (default, unchanged), 'sslcommerz', or
// 'fastspring' (international/USD). The entire UI/packages/coupon flow is identical — only the
// checkout call differs — so the same component powers all three tabs.
export default function BuyCreditTab({ gateway = 'moneybag' }) {
    const { t, i18n } = useTranslation();
    // Currency symbol: FastSpring packages ('others' type) are priced in USD; the BD
    // gateways (Moneybag/SSLCommerz) are priced in BDT taka.
    const curr = gateway === 'fastspring' ? '$' : '৳';
    // ISO currency code for the badge shown under each package amount — mirrors `curr`
    // ($ → USD, ৳ → BDT so BD / empty-country users see "BDT").
    const currencyCode = curr === '$' ? 'USD' : 'BDT';
    const fmtNum = (n) => n.toLocaleString(i18n.language === 'bn' ? 'bn-BD' : 'en-US');
    // Convert ASCII digits in a string (e.g. "01:23", "0.50") to Bangla numerals when in bn.
    const bnDigit = (s) => i18n.language === 'bn' ? String(s).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[+d]) : String(s);

    // ── Free-module offer helpers ──────────────────────────────────────────────
    const moduleLabel = (mod) => (mod === 'IMAGE_MANUAL' ? t('payments.actionManualEdit') : mod);
    const freeDuration = (months) => {
        if (months % 12 === 0) {
            const y = months / 12;
            return i18n.language === 'bn' ? `${bnDigit(y)} বছর` : `${y} year${y > 1 ? 's' : ''}`;
        }
        return i18n.language === 'bn' ? `${bnDigit(months)} মাস` : `${months} month${months > 1 ? 's' : ''}`;
    };
    // Free-module grant a package's BASE price (`amount`) qualifies for (widest match), or null.
    const freeGrantForPackage = (pkg) => {
        const amt = Number(pkg?.amount);
        if (!Number.isFinite(amt)) return null;
        let best = null;
        for (const r of (freeOfferRules || [])) {
            if (amt >= r.min && amt <= r.max && (!best || r.months > best.months)) best = r;
        }
        return best;
    };
    const { user, getCurrentUser } = useAuthStore();
    const isMobile = useMediaQuery('(max-width: 768px)');

    // ── Recent buyers social-proof feed (max 10, rotates one at a time) ──
    // Backend serves this from a 30-min cache, so it's not realtime by design.
    const [recentBuyers, setRecentBuyers] = useState([]);
    const [monthlyBuyers, setMonthlyBuyers] = useState(0);
    const [todayBuyers, setTodayBuyers] = useState(0);
    const [buyerIdx, setBuyerIdx] = useState(0);
    useEffect(() => {
        paymentService.getRecentBuyers()
            .then((data) => {
                setRecentBuyers(Array.isArray(data?.buyers) ? data.buyers : []);
                // Today's / this-month's buyer counts only ever grow within the period, so never
                // let a stale or lower response roll the social-proof number backward. Starts from
                // 0, so the first fetch is unaffected; guards any later refetch. (The backend now
                // serves consistent fresh counts across cluster instances — this is belt-and-braces.)
                setMonthlyBuyers(v => Math.max(v, Number.isFinite(data?.monthlyBuyers) ? data.monthlyBuyers : 0));
                setTodayBuyers(v => Math.max(v, Number.isFinite(data?.todayBuyers) ? data.todayBuyers : 0));
            })
            .catch(() => {});
    }, []);
    // The current 30-minute promo slot. Re-checked every minute; setState bails out when the
    // slot is unchanged, so the fake pair only swaps on a real 30-min boundary (even if the
    // page stays open). Each page load also picks up the current slot.
    const [promoSlot, setPromoSlot] = useState(() => currentSlotIndex());
    useEffect(() => {
        const id = setInterval(() => setPromoSlot(currentSlotIndex()), 60_000);
        return () => clearInterval(id);
    }, []);
    // Real buyers + this slot's 2 promo buyers, interleaved (social proof, marketing-only —
    // see config/socialProofBuyers). Real data stays untouched; this only augments what rotates.
    const displayBuyers = useMemo(
        () => withFakeBuyers(recentBuyers, promoSlot),
        [recentBuyers, promoSlot]
    );
    useEffect(() => {
        if (displayBuyers.length <= 1) return;
        const id = setInterval(() => setBuyerIdx(i => (i + 1) % displayBuyers.length), 3000);
        return () => clearInterval(id);
    }, [displayBuyers.length]);
    const currentBuyer = displayBuyers[buyerIdx % Math.max(1, displayBuyers.length)] ?? null;

    // Footer stat rotates between today's and this month's exact buyer counts. Today is only
    // included when there's at least one buyer, so quiet mornings never show "0 bought today".
    const buyerStats = useMemo(() => {
        const s = [];
        if (todayBuyers > 0) s.push({ count: todayBuyers, suffix: t('payments.buyersTodaySuffix') });
        if (monthlyBuyers > 0) s.push({ count: monthlyBuyers, suffix: t('payments.buyersSuffix') });
        return s;
    }, [todayBuyers, monthlyBuyers, t]);
    const [statIdx, setStatIdx] = useState(0);
    useEffect(() => {
        if (buyerStats.length <= 1) return;
        const id = setInterval(() => setStatIdx(i => (i + 1) % buyerStats.length), 3000);
        return () => clearInterval(id);
    }, [buyerStats.length]);
    const currentStat = buyerStats[statIdx % Math.max(1, buyerStats.length)] ?? null;

    const [allPackages, setAllPackages]         = useState([]);
    const [loadingPackages, setLoadingPackages] = useState(true);
    const [selectedId, setSelectedId]           = useState(null);
    const [couponCode, setCouponCode]           = useState('');
    const [couponState, setCouponState]         = useState(null);
    const [couponValidMap, setCouponValidMap]   = useState({});
    const [couponError, setCouponError]         = useState('');
    const [validatingCoupon, setValidatingCoupon] = useState(false);
    const [purchasing, setPurchasing]           = useState(false);
    const [purchasingChannel, setPurchasingChannel] = useState(null); // which button shows the spinner
    // SSL tab shows a terms checkbox that starts UNCHECKED (user must agree). The Moneybag
    // tab has no checkbox, so it starts agreed=true to keep its pay button enabled.
    const [agreedToTerms, setAgreedToTerms]     = useState(gateway !== 'sslcommerz');
    // The component can mount before the SSLCommerz tab is the active gateway (the prop changes
    // afterwards), which left the box pre-ticked. Re-derive whenever the gateway changes so the SSL
    // tab always starts unchecked.
    useEffect(() => { setAgreedToTerms(gateway !== 'sslcommerz'); }, [gateway]);
    // The pay buttons stay clickable. Pressing one without the tick doesn't pay: it shakes the terms row (label + checkbox)
    // and moves focus onto the checkbox so the user sees what is missing.
    const termsLabelRef = useRef(null);
    const termsBoxRef = useRef(null);
    const remindTerms = () => {
        termsBoxRef.current?.focus({ preventScroll: true });
        const row = termsLabelRef.current;
        if (!row || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return; // reduced motion: the focus ring is the cue
        row.classList.remove('shake');
        void row.offsetWidth; // restart the shake when the button is pressed again mid-animation
        row.classList.add('shake'); // .shake = the shared shake in global.scss; cleared by onAnimationEnd on the label
    };
    const [liveCosts, setLiveCosts]             = useState(null);
    // Free-module offer rules (price-range → module + months). These are GLOBAL (same for
    // every user) — this page previews what each PACKAGE offers, independent of the viewer's
    // own grants. The actual free benefit is applied server-side only after purchase + grant.
    const [freeOfferRules, setFreeOfferRules]   = useState([]);

    useEffect(() => { usageService.getCreditCosts().then(setLiveCosts).catch(() => {}); }, []);
    useEffect(() => {
        usageService.getCreditConfig()
            .then((cfg) => setFreeOfferRules(cfg?.moduleFreeOfferRules || []))
            .catch(() => {});
    }, []);

    // The user's market: non-Bangladesh users buy internationally ('others' in USD), BD users
    // (or those with no country set) buy locally ('bangladesh' in BDT). `market` is the currency
    // view sent to the API so dual-market ('all') packages price in the right currency.
    const regionType = (!user?.country || user.country.toUpperCase() === 'BD') ? 'bangladesh' : 'others';
    const market = regionType === 'others' ? 'intl' : 'bd';

    // Refetch when the market resolves/changes so 'all' packages come back priced for it.
    useEffect(() => {
        setLoadingPackages(true);
        planService.getPackages(market)
            .then((data) => setAllPackages(data))
            .catch(() => {})
            .finally(() => setLoadingPackages(false));
    }, [market]);

    // Show the packages for the user's market, plus dual-market 'all' packages (visible to both).
    const packages = useMemo(
        () => allPackages.filter(p => {
            const type = p.packageType || 'bangladesh';
            return type === regionType || type === 'all';
        }),
        [allPackages, regionType],
    );

    // Credits are priced at "1 cent = 1 credit". For BD packages basePrice is in ৳
    // (1 credit = 1৳, factor 1); for `others` basePrice is in $ (1 credit = 1¢, so the
    // base credit-equivalent is basePrice × 100). Using this factor keeps the bonus =
    // (credits − base credit-equivalent) correct for both markets.
    const creditUnit = regionType === 'others' ? 100 : 1;

    // Default the selection to the region's popular (or middle) package, keeping a
    // still-valid choice if the user already picked one within this region.
    useEffect(() => {
        if (loadingPackages) return;
        setSelectedId(prev => {
            if (prev && packages.some(p => p.id === prev)) return prev;
            const popular = packages.find(p => p.isPopular);
            return popular?.id ?? packages[Math.floor(packages.length / 2)]?.id ?? null;
        });
    }, [packages, loadingPackages]);

    const handleSelectPackage = (pkg) => {
        if (pkg.id === selectedId) return;
        setSelectedId(pkg.id);
        setCouponError('');
        if (Object.keys(couponValidMap).length > 0) {
            setCouponState(couponValidMap[pkg.id] ?? null);
        } else {
            setCouponState(null);
            setCouponCode('');
        }
    };

    // "What you can do" is computed with the NEW wallet costs (1 credit = 1tk), not the
    // legacy per-action costs — buyers land in the wallet system, so the breakdown must match.
    const costs    = liveCosts?.wallet ?? WALLET_CREDIT_COSTS;
    const selected = packages.find(p => p.id === selectedId);

    const allActions = useMemo(
        () => packages.reduce((acc, pkg) => {
            acc[pkg.id] = calculateActionsBreakdown(pkg.credits, costs);
            return acc;
        }, {}),
        [packages, costs]
    );

    const activeCoupon    = couponState?.betterThanDefault ? couponState : null;
    const discountPercent = activeCoupon?.discountPercent ?? selected?.discountPercent ?? 0;
    const finalPrice      = activeCoupon?.finalPrice      ?? selected?.finalPrice      ?? 0;
    const basePrice       = selected?.basePrice ?? selected?.credits ?? 0;
    const savingAmount    = basePrice - finalPrice;
    // Free bonus credits on the selected package (credits beyond the base price).
    const bonusCredits    = Math.max(0, (selected?.credits ?? 0) - basePrice * creditUnit);
    const actions         = selected ? (allActions[selected.id] ?? { images: 0, documents: 0 }) : { images: 0, documents: 0 };

    // Per-credit price helper + best-value package. "Best value" = the lowest per-credit
    // price (finalPrice / credits), i.e. the true best rate — matching the "প্রতি ক্রেডিট"
    // figure on each card. Ties are broken toward the bigger package (more credits).
    const perCreditStr = (pkg) => {
        const rate = pkg.credits ? pkg.finalPrice / pkg.credits : 0;
        // USD credits are priced at a 1-cent base; bonus credits can drop the effective
        // rate below a cent, which would round to "$0.00" and read as free. Floor the USD
        // display at $0.01 so it never shows less than one cent. (BDT is shown as-is.)
        const shown = curr === '$' ? Math.max(rate, 0.01) : rate;
        return bnDigit(shown.toFixed(2));
    };
    const bestValueId = useMemo(() => {
        let best = null, minRate = Infinity, bestCredits = -Infinity;
        packages.forEach((p) => {
            if (!p.credits) return;
            const rate = p.finalPrice / p.credits;
            if (rate < minRate || (rate === minRate && p.credits > bestCredits)) {
                minRate = rate; best = p.id; bestCredits = p.credits;
            }
        });
        return best;
    }, [packages]);
    const bestPkg = packages.find(p => p.id === bestValueId);

    const handleApplyCoupon = async () => {
        if (!couponCode.trim() || packages.length === 0) return;
        setValidatingCoupon(true);
        setCouponError('');
        setCouponState(null);
        setCouponValidMap({});
        const results = await Promise.allSettled(
            packages.map(pkg => planService.validateCoupon({ code: couponCode.trim(), planId: pkg.id, market }))
        );
        const validMap = {};
        results.forEach((result, i) => {
            if (result.status === 'fulfilled') validMap[packages[i].id] = result.value;
        });
        if (Object.keys(validMap).length === 0) {
            const firstErr = results.find(r => r.status === 'rejected');
            setCouponError(firstErr?.reason?.response?.data?.message || firstErr?.reason?.message || t('payments.couponInvalid'));
        } else {
            setCouponValidMap(validMap);
            setCouponState(validMap[selectedId] ?? null);
        }
        setValidatingCoupon(false);
    };

    const handleRemoveCoupon = () => { setCouponState(null); setCouponError(''); setCouponCode(''); setCouponValidMap({}); };

    // FastSpring settles via our backend webhook (no redirect), so after the popup
    // closes we poll our own status endpoint, then refresh the wallet balance on success.
    const pollFastSpringStatus = async (orderId) => {
        for (let i = 0; i < 20; i++) {
            try {
                const s = await paymentService.getStatus(orderId);
                if (s?.status === 'SUCCESS') {
                    await getCurrentUser?.().catch(() => {});
                    notifications.show({
                        title: t('payments.notifSuccessTitle'),
                        message: t('payments.notifSuccessMsg', { credits: s.credits }),
                        color: 'green',
                    });
                    return;
                }
                if (s?.status === 'FAILED' || s?.status === 'CANCELLED') return;
            } catch { /* keep polling — webhook may still be in flight */ }
            await new Promise((r) => setTimeout(r, 2000));
        }
    };

    // `channel` ('bkash' | 'nagad', or 'rocket' once it is switched on — see `directPay`) is the optional direct-pay shortcut on the SSLCommerz tab.
    // The main Pay button passes the click event here, so only a string counts as a channel.
    const handlePurchase = async (channel) => {
        if (!selected) return;
        if (!agreedToTerms) { remindTerms(); return; } // the terms tick is required: point at it instead of paying
        const ch = typeof channel === 'string' ? channel : undefined;
        try {
            setPurchasing(true);
            setPurchasingChannel(ch || null);
            if (gateway === 'fastspring') {
                // International (USD) checkout: create a session, open the FastSpring popup,
                // then confirm via our backend once the popup signals completion/close.
                const { sessionId, orderId } = await paymentService.createCheckoutFastSpring({
                    planId: selected.id,
                    couponCode: activeCoupon?.code || null,
                });
                const onDone = () => pollFastSpringStatus(orderId);
                window.__fsOnPopupClosed = onDone;
                window.__fsOnPopupWebhook = onDone;
                const opened = await openFastSpringPopup(sessionId);
                if (!opened) throw new Error('fastspring popup unavailable');
                setPurchasing(false); // popup is up; re-enable the button behind it
                return;
            }
            if (gateway === 'sslcommerz') {
                // SSLCommerz: create the session, then open the official EasyCheckout popup.
                // Fall back to a full-page redirect if the popup can't be triggered.
                const { orderId, redirectUrl } = await paymentService.createCheckoutSSL({
                    planId: selected.id,
                    couponCode: activeCoupon?.code || null,
                    channel: ch,
                });
                const usePopup = SSLCZ_USE_POPUP && SSLCZ_CAN_POPUP;
                const opened = usePopup && await openSslczPopup(orderId);
                if (!opened) window.location.href = redirectUrl;
                else setPurchasing(false); // popup is up; re-enable the button behind it
                return;
            }
            const { redirectUrl } = await paymentService.createCheckout({
                planId: selected.id,
                couponCode: activeCoupon?.code || null,
            });
            window.location.href = redirectUrl;
        } catch {
            // Show a friendly message only — never surface the raw backend/validation
            // error (e.g. Moneybag's JSON "Invalid Bangladeshi operator code") to the user.
            notifications.show({
                title: t('payments.notifCheckoutFailedTitle'),
                message: t('payments.notifCheckoutFailedMsg'),
                color: "red",
            });
            setPurchasing(false);
        }
    };

    // Direct-pay shortcuts on the SSLCommerz tab: each opens SSLCommerz already filtered to that one wallet.
    // Rocket is OFF for now (2026-10-08): the SSLCommerz store has no Rocket (DBBL Mobile Banking) enabled, so SSLCommerz ignores the
    // filter and drops the customer on its generic card checkout. Remove `off: true` once SSLCommerz switches Rocket on for the store.
    // Heads-up when you do: three buttons need ~306px but the phone group is only 283px at a 375px viewport, so tighten the phone padding first.
    const directPay = [
        { id: 'bkash',  label: t('payments.payViaBkash'),  tip: t('payments.payViaBkashTip'),  logo: '/images/payment/bkash.svg',  cls: 'border-pink-600 hover:bg-pink-50' },
        { id: 'nagad',  label: t('payments.payViaNagad'),  tip: t('payments.payViaNagadTip'),  logo: '/images/payment/nagad.svg',  cls: 'border-red-600 hover:bg-red-50' },
        { id: 'rocket', label: t('payments.payViaRocket'), tip: t('payments.payViaRocketTip'), logo: '/images/payment/rocket.svg', cls: 'border-fuchsia-800 hover:bg-fuchsia-50', off: true },
    ].filter((c) => !c.off);

    if (loadingPackages) {
        // Structure-matching skeleton (mirrors the real layout below) so the page doesn't
        // collapse/jump while packages load — a bare centered spinner broke the layout.
        return (
            <div className="space-y-5">
                {/* Top: balance + recent buyers */}
                <div className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-4 sm:gap-5 items-stretch`}>
                    <div className="flex-[1.4] rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 sm:px-5 py-4 flex items-center gap-3 sm:gap-4">
                        <div className={`${SK} w-11 h-11 rounded-xl flex-shrink-0`} />
                        <div className="min-w-0 space-y-2">
                            <div className={`${SK} h-3 w-28`} />
                            <div className={`${SK} h-6 w-24`} />
                        </div>
                    </div>
                    <div className="flex-1 rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-5 py-4 space-y-3">
                        <div className={`${SK} h-3 w-32`} />
                        <div className="flex gap-2">
                            {Array.from({ length: 4 }).map((_, i) => <div key={i} className={`${SK} h-9 w-12`} />)}
                        </div>
                    </div>
                </div>

                {/* Packages + summary */}
                <div className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-5 items-start`}>
                    {/* LEFT: package rows */}
                    <div className={isMobile ? "w-full" : "flex-[1.4]"}>
                        <div className="flex items-baseline justify-between mb-3">
                            <div className={`${SK} h-5 w-40`} />
                            <div className={`${SK} h-3 w-24`} />
                        </div>
                        <div className="flex flex-col gap-2.5">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <div key={i} className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-3.5 sm:p-4 flex items-center gap-3 sm:gap-4">
                                    <div className={`${SK} w-[22px] h-[22px] rounded-full flex-shrink-0`} />
                                    <div className="flex-1 min-w-0 space-y-2">
                                        <div className={`${SK} h-4 w-32`} />
                                        <div className={`${SK} h-3 w-20`} />
                                    </div>
                                    <div className="text-right space-y-2 flex-shrink-0">
                                        <div className={`${SK} h-5 w-16 ml-auto`} />
                                        <div className={`${SK} h-3 w-12 ml-auto`} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* RIGHT: summary panel */}
                    <div className={isMobile ? "w-full" : "flex-1"}>
                        <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-5 space-y-4">
                            <div className={`${SK} h-5 w-40`} />
                            <div className="space-y-2.5">
                                {Array.from({ length: 3 }).map((_, i) => <div key={i} className={`${SK} h-4 w-full`} />)}
                            </div>
                            <div className={`${SK} h-px w-full`} />
                            <div className="flex items-center justify-between">
                                <div className={`${SK} h-5 w-24`} />
                                <div className={`${SK} h-8 w-28`} />
                            </div>
                            <div className={`${SK} h-12 w-full rounded-xl`} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    if (!selected) return null;

    const actionsList = [
        { module: 'IMAGE', icon: <IconPhoto size={15} />, bg: "bg-orange-100 dark:bg-orange-900/40", color: "text-orange-600 dark:text-orange-400", count: actions.images, label: t('payments.actionImage') },
        { module: 'IMAGE_MANUAL', icon: <IconPhotoEdit size={15} />, bg: "bg-emerald-100 dark:bg-emerald-900/40", color: "text-emerald-600 dark:text-emerald-400", count: actions.manualEdits, label: t('payments.actionManualEdit') },
        { module: 'BULK_PHOTO', icon: <IconStack2 size={15} />, bg: "bg-rose-100 dark:bg-rose-900/40", color: "text-rose-600 dark:text-rose-400", count: actions.bulkPhotos, label: t('payments.actionBulkEdit') },
        { module: 'DOCUMENT', icon: <IconFileDescription size={15} />, bg: "bg-amber-100 dark:bg-amber-900/40", color: "text-amber-600 dark:text-amber-400", count: actions.documents, label: t('payments.actionDocument') },
        { module: 'AI_TEMPLATE', icon: <IconSparkles size={15} />, bg: "bg-purple-100 dark:bg-purple-900/40", color: "text-purple-600 dark:text-purple-400", count: actions.aiTemplates, label: t('payments.actionAiTemplate') },
        { module: 'PRINT', icon: <IconPrinter size={15} />, bg: "bg-sky-100 dark:bg-sky-900/40", color: "text-sky-600 dark:text-sky-400", count: actions.prints, label: t('payments.actionPrint') },
    ];

    // The module the SELECTED package would grant free access to. The "what you can do"
    // breakdown reflects THIS package only — a package that doesn't qualify must NOT show a
    // module as free even if the user happens to hold an active grant from an earlier purchase.
    const selectedFreeModule = selected ? freeGrantForPackage(selected)?.module : null;
    // For how long the SELECTED package grants the module free (used in the breakdown label).
    const freeMonthsForModule = (mod) => {
        if (selected && selectedFreeModule === mod) return freeGrantForPackage(selected)?.months ?? null;
        return null;
    };
    // Free modules aren't part of the "or" choice (they're always available), so they list
    // at the top; the remaining paid actions are the mutually-exclusive "অথবা" chain. Only the
    // SELECTED package's grant counts here — not the user's pre-existing active grant.
    const isActionFree = (item) => !!item.module && selectedFreeModule === item.module;
    const freeActions = actionsList.filter(isActionFree);
    const paidActions = actionsList.filter((it) => !isActionFree(it));

    // Always-free platform features that come with any package — listed in the
    // breakdown (with a "ফ্রি" badge) so buyers see the extra value on top of the
    // credit-spend choice below. Mirrors the free tool cards shown elsewhere.
    const freeExtras = [
        { key: 'questionCreate', icon: <IconClipboardList size={15} />, bg: "bg-rose-100 dark:bg-rose-900/40", color: "text-rose-600 dark:text-rose-400", label: t('profile.creditModule_QUESTION_CREATE') },
        { key: 'questionExport', icon: <IconFileExport size={15} />, bg: "bg-teal-100 dark:bg-teal-900/40", color: "text-teal-600 dark:text-teal-400", label: t('profile.creditModule_QUESTION_EXPORT') },
        { key: 'formAutoFill', icon: <IconForms size={15} />, bg: "bg-indigo-100 dark:bg-indigo-900/40", color: "text-indigo-600 dark:text-indigo-400", label: t('navigation.formAutoFillup') },
        { key: 'tools', icon: <IconTools size={15} />, bg: "bg-fuchsia-100 dark:bg-fuchsia-900/40", color: "text-fuchsia-600 dark:text-fuchsia-400", label: t('profile.creditToolsLabel') },
    ];

    // Post-payment explainer steps
    const paymentSteps = [
        { n: 1, title: t('payments.stepPaidTitle'),   desc: t('payments.stepPaidDesc') },
        { n: 2, title: t('payments.stepCreditTitle'), desc: t('payments.stepCreditDesc') },
        { n: 3, title: t('payments.stepStartTitle'),  desc: t('payments.stepStartDesc') },
    ];

    const visiblePackages = Object.keys(couponValidMap).length > 0
        ? packages.filter(pkg => !!couponValidMap[pkg.id])
        : packages;

    return (
        <div className="space-y-5">
            {/* Hidden host elements for the SSLCommerz EasyCheckout popup. They MUST be in the
                DOM before embed.min.js loads (the widget binds to #sslczPayBtn at init), so they
                render here at the top of the buy UI. openSslczPopup() sets the attrs and clicks. */}
            {gateway === 'sslcommerz' && SSLCZ_USE_POPUP && (
                <>
                    <form id="myForm" className="hidden" />
                    <button id="sslczPayBtn" type="button" className="hidden" aria-hidden="true" tabIndex={-1} />
                </>
            )}

            {/* ── Top: balance + recent buyers ── */}
            <div className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-4 sm:gap-5 items-stretch`}>
                {user && (
                    <div className="flex-[1.4] bg-gradient-to-br from-orange-50 to-amber-50/40 dark:from-orange-950/30 dark:to-zinc-900 border border-orange-200 dark:border-orange-900/50 rounded-2xl px-4 sm:px-5 py-4 flex items-center gap-3 sm:gap-4">
                        <div className="w-11 h-11 rounded-xl bg-white dark:bg-zinc-800 border border-orange-200 dark:border-orange-900/50 flex items-center justify-center text-primary flex-shrink-0">
                            <IconCoins size={22} />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs text-orange-800/70 dark:text-orange-300/70">{t('payments.currentBalance') || 'বর্তমান ক্রেডিট ব্যালেন্স'}</p>
                            {(() => {
                                const { showBoth, showLegacyOnly, legacy, wallet } = creditParts(user);
                                if (showBoth) {
                                    return (
                                        <p className="flex items-baseline gap-1.5 leading-none flex-wrap">
                                            <span className="text-lg font-extrabold text-zinc-800 dark:text-zinc-100 tabular-nums">{t('profile.creditOldLabel')} {fmtNum(legacy)}</span>
                                            <span className="text-zinc-300">•</span>
                                            <span className="text-lg font-extrabold text-primary tabular-nums">{t('profile.creditNewLabel')} {fmtNum(wallet)}</span>
                                        </p>
                                    );
                                }
                                // Single pool (legacy-only OR wallet-only): show that balance with the
                                // plain "ক্রেডিট" label — the "পুরনো" qualifier is only needed when BOTH
                                // pools are shown together (the showBoth branch above).
                                return (
                                    <p className="flex items-baseline gap-1.5 leading-none">
                                        <span className="text-2xl font-extrabold text-zinc-800 dark:text-zinc-100 tabular-nums">{fmtNum(showLegacyOnly ? legacy : wallet)}</span>
                                        <span className="text-sm font-semibold text-zinc-400">{t('payments.creditsLabel') || 'ক্রেডিট'}</span>
                                    </p>
                                );
                            })()}
                        </div>
                        {bestPkg && (
                            <div className="ml-auto text-right border-l border-orange-200 dark:border-orange-900/50 pl-4 hidden sm:block">
                                <p className="text-[11px] text-orange-800/70 dark:text-orange-300/70">{t('payments.bestPriceToday')}</p>
                                <p className="text-base font-bold text-zinc-800 dark:text-zinc-100">{t('payments.perCreditLabel')} {curr}{perCreditStr(bestPkg)}</p>
                            </div>
                        )}
                    </div>
                )}
                <div className="flex-1 bg-zinc-800 dark:bg-zinc-950 text-white rounded-2xl px-5 py-4 flex flex-col justify-center gap-2.5">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-orange-300">
                        <IconUsers size={15} /> {t('payments.recentBuyersTitle')}
                    </div>

                    {currentBuyer ? (
                        // key forces a remount on each rotation so the fade-in replays.
                        <div key={buyerIdx} className="flex items-center gap-3 animate-in fade-in slide-in-from-bottom-1 duration-500">
                            {currentBuyer.avatar ? (
                                <img
                                    src={currentBuyer.avatar}
                                    alt=""
                                    className="w-10 h-10 rounded-full object-cover border-2 border-zinc-700 flex-shrink-0"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                            ) : (
                                <span className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-white border-2 border-zinc-700 flex-shrink-0 uppercase">
                                    {currentBuyer.fullName?.charAt(0) || '?'}
                                </span>
                            )}
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold leading-tight truncate text-orange-300">
                                    {currentBuyer.fullName}
                                </p>
                                {(currentBuyer.district || currentBuyer.shopName) && (
                                    <p className="flex items-center gap-1 text-[11px] text-zinc-400 mt-0.5 truncate">
                                        <IconMapPin size={11} className="flex-shrink-0" />
                                        <span className="truncate">
                                            {[currentBuyer.shopName, currentBuyer.district].filter(Boolean).join(' · ')}
                                        </span>
                                    </p>
                                )}
                            </div>
                            {currentBuyer.credits ? (
                                <div className="ml-auto text-right flex-shrink-0 leading-tight">
                                    <p className="text-lg font-extrabold text-emerald-400 tabular-nums">
                                        {fmtNum(currentBuyer.credits)} <span className="text-xs font-bold">{t('payments.buyersCreditsUnit')}</span>
                                    </p>
                                    <p className="text-[10px] font-semibold text-zinc-400 mt-0.5">{t('payments.recentBuyerPurchased')}</p>
                                </div>
                            ) : (
                                <span className="ml-auto flex items-center gap-1.5 text-[10px] font-semibold text-emerald-400 flex-shrink-0">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {t('payments.recentBuyerPurchased')}
                                </span>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-xs text-zinc-400 py-1.5">
                            <IconUsers size={16} /> {t('payments.recentBuyersEmpty')}
                        </div>
                    )}

                    {currentStat && (
                        <div className="flex items-center gap-2 text-xs text-zinc-300 border-t border-zinc-700/60 pt-2.5">
                            <div className="flex">
                                <span className="w-5 h-5 rounded-full bg-orange-300 border-2 border-zinc-800 dark:border-zinc-950" />
                                <span className="w-5 h-5 rounded-full bg-emerald-300 border-2 border-zinc-800 dark:border-zinc-950 -ml-2" />
                                <span className="w-5 h-5 rounded-full bg-blue-300 border-2 border-zinc-800 dark:border-zinc-950 -ml-2" />
                            </div>
                            {/* key replays the fade each time it swaps today ⇄ this month */}
                            <span key={statIdx} className="animate-in fade-in duration-500">
                                <b className="text-white">{fmtNum(currentStat.count)}</b> {currentStat.suffix}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Packages + summary ── */}
            <div className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-5 items-start`}>

                {/* LEFT: Package cards */}
                <div className={isMobile ? "w-full" : "flex-[1.4]"}>
                    <div className="flex items-baseline justify-between mb-3">
                        <h3 className="text-base sm:text-lg font-bold text-zinc-800 dark:text-zinc-100">{t('payments.choosePackage')}</h3>
                        <p className="text-[11px] sm:text-xs font-semibold text-emerald-600 dark:text-emerald-400">{t('payments.biggerCheaper')}</p>
                    </div>
                    <div className="flex flex-col gap-2.5">
                        {visiblePackages.map((pkg) => {
                            const isSelected      = pkg.id === selectedId;
                            const freeGrant       = freeGrantForPackage(pkg);
                            const cardCoupon      = couponValidMap[pkg.id];
                            const couponOnCard    = cardCoupon?.betterThanDefault ? cardCoupon : null;
                            const displayFinal    = couponOnCard ? couponOnCard.finalPrice     : pkg.finalPrice;
                            const displayDiscount = couponOnCard ? couponOnCard.discountPercent : pkg.discountPercent;
                            const saving          = pkg.basePrice - displayFinal;
                            // Bonus = credits beyond the base price's credit-equivalent. Kept
                            // separate from the discount — it's the free extra credits.
                            const bonus           = Math.max(0, (pkg.credits ?? 0) - (pkg.basePrice ?? 0) * creditUnit);

                            return (
                                <button
                                    type="button"
                                    key={pkg.id}
                                    onClick={() => handleSelectPackage(pkg)}
                                    className={`relative w-full text-left rounded-2xl border p-3.5 sm:p-4 flex items-center gap-3 sm:gap-4 transition-all ${
                                        isSelected
                                            ? "border-primary bg-primary/5 dark:bg-primary/10 ring-1 ring-primary"
                                            : "border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:border-zinc-300 dark:hover:border-zinc-600"
                                    }`}
                                >
                                    {/* Radio */}
                                    <span className={`w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? "border-primary" : "border-zinc-300 dark:border-zinc-600"}`}>
                                        {isSelected && <span className="w-[11px] h-[11px] rounded-full bg-primary" />}
                                    </span>

                                    {/* Middle */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 sm:gap-2.5 flex-wrap">
                                            <span className="text-xl sm:text-2xl font-extrabold text-zinc-800 dark:text-zinc-100 leading-none tabular-nums">{fmtNum(pkg.credits)}</span>
                                            <span className="text-xs text-zinc-400">{t('payments.creditsLabel')}</span>
                                            {pkg.isPopular && (
                                                <span className="inline-flex items-center gap-1 bg-orange-100 dark:bg-orange-900/30 text-primary border border-orange-200 dark:border-orange-900/50 rounded-full px-2 py-0.5 text-[11px] font-bold"><IconStar size={11} /> {t('payments.mostPopular')}</span>
                                            )}
                                            {!pkg.isPopular && pkg.id === bestValueId && (
                                                <span className="inline-flex items-center gap-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/50 rounded-full px-2 py-0.5 text-[11px] font-bold"><IconDiamond size={11} /> {t('payments.bestValue')}</span>
                                            )}
                                            {bonus > 0 && (
                                                <span className="inline-flex items-center gap-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/50 rounded-full px-2 py-0.5 text-[11px] font-bold"><IconGift size={11} /> {t('payments.bonusBadge', { n: bnDigit(bonus) })}</span>
                                            )}
                                        </div>
                                        {pkg.name && <p className="text-xs font-bold text-zinc-500 dark:text-zinc-400 mt-1">{pkg.name} </p>}
                                        <span className="inline-flex items-center mt-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-md px-2 py-0.5 text-[11px] font-semibold">{t('payments.perCreditLabel')} {curr}{perCreditStr(pkg)}</span>
                                    </div>

                                    {/* Right */}
                                    <div className="text-right flex-shrink-0">
                                        {freeGrant && (
                                            <div className="flex justify-end mb-1.5">
                                                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-extrabold text-white shadow-md bg-gradient-to-r from-emerald-500 to-teal-500">
                                                    <IconGift size={12} /> {moduleLabel(freeGrant.module)} {t('profile.creditFree')} · {freeDuration(freeGrant.months)}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 justify-end">
                                            {pkg.basePrice !== displayFinal && (
                                                <span className="text-xs text-zinc-400 line-through">{curr}{fmtNum(pkg.basePrice)}</span>
                                            )}
                                            {displayDiscount > 0 && (
                                                <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full px-2 py-0.5 text-[11px] font-bold">{bnDigit(displayDiscount)}% {t('payments.off')}</span>
                                            )}
                                        </div>
                                        <div className="text-xl sm:text-2xl font-extrabold text-zinc-800 dark:text-zinc-100 mt-1 leading-none tabular-nums">{curr}{fmtNum(displayFinal)}</div>
                                        {saving > 0 && (
                                            <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">{curr}{fmtNum(saving)} {t('payments.saveWord')}</div>
                                        )}
                                        {/* Currency code badge — tells the buyer the price is in BDT or USD */}
                                        <div className="mt-1.5 flex justify-end">
                                            <span className="inline-flex items-center rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 px-1.5 py-0.5 text-[10px] font-bold tracking-wide">
                                                {currencyCode}
                                            </span>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* RIGHT: Sticky summary */}
                <div className={isMobile ? "w-full" : "flex-1 sticky top-5"}>
                    <div className="border border-zinc-200 dark:border-zinc-700 rounded-2xl overflow-clip bg-white dark:bg-zinc-900 shadow-sm">
                        {/* Selected header */}
                        <div className="px-5 py-3.5 bg-gradient-to-br from-orange-50 to-amber-50/40 dark:from-orange-950/30 dark:to-zinc-900 border-b border-orange-200/70 dark:border-orange-900/40">
                            <div className="flex items-center gap-2">
                                <p className="text-xs text-orange-800/70 dark:text-orange-300/70 truncate">
                                    {t('payments.yourSelection')}{selected.name ? ` · ${selected.name} ${t('payments.packageWord')}` : ''}
                                </p>
                                <span className="ml-auto flex items-center gap-1.5 flex-shrink-0">
                                    {bonusCredits > 0 && (
                                        <span className="inline-flex items-center gap-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full px-2.5 py-0.5 text-[11px] font-bold"><IconGift size={11} /> {t('payments.bonusBadge', { n: bnDigit(bonusCredits) })}</span>
                                    )}
                                    {discountPercent > 0 && (
                                        <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full px-2.5 py-0.5 text-[11px] font-bold">{bnDigit(discountPercent)}% {t('payments.off')}</span>
                                    )}
                                </span>
                            </div>
                            <div className="flex items-baseline gap-2 mt-0.5">
                                <span className="text-[27px] font-extrabold text-primary leading-none tabular-nums">{fmtNum(selected.credits)}</span>
                                <span className="text-sm text-zinc-400">{t('payments.creditsLabel')}</span>
                            </div>
                        </div>

                        {/* What you can do */}
                        <div className="px-5 pt-3.5 pb-1">
                            {/* Free modules — always available, shown ABOVE the header, NOT part of the "or" choice */}
                            {(freeActions.length > 0 || freeExtras.length > 0) && (
                                <div className="flex flex-col mb-2.5 pb-2.5 border-b border-zinc-100 dark:border-zinc-800">
                                    {freeActions.map((item) => (
                                        <div key={item.module} className="flex items-center gap-2.5 py-0.5">
                                            <div className={`w-[26px] h-[26px] rounded-lg ${item.bg} ${item.color} flex items-center justify-center flex-shrink-0`}>{item.icon}</div>
                                            <span className="text-[13px] text-zinc-600 dark:text-zinc-300">{item.label}</span>
                                            <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] font-extrabold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                                                {t('profile.creditFree')}{freeMonthsForModule(item.module) ? ` · ${freeDuration(freeMonthsForModule(item.module))}` : ''}
                                            </span>
                                        </div>
                                    ))}
                                    {freeExtras.map((item) => (
                                        <div key={item.key} className="flex items-center gap-2.5 py-0.5">
                                            <div className={`w-[26px] h-[26px] rounded-lg ${item.bg} ${item.color} flex items-center justify-center flex-shrink-0`}>{item.icon}</div>
                                            <span className="text-[13px] text-zinc-600 dark:text-zinc-300">{item.label}</span>
                                            <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] font-extrabold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                                                {t('profile.creditFree')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="flex items-center gap-2 mb-2">
                                <p className="text-xs font-bold text-zinc-700 dark:text-zinc-200">{t('payments.whatCanDo')}</p>
                                <span className="ml-auto inline-flex items-center gap-1 bg-orange-50 dark:bg-orange-900/20 border border-orange-200/70 dark:border-orange-900/40 text-amber-700 dark:text-amber-400 rounded-full px-2 py-0.5 text-[10px] font-bold">
                                    <IconInfoCircle size={11} /> {t('payments.anyOne')}
                                </span>
                            </div>
                            <div className="flex flex-col">
                                {/* Paid actions — pick ONE (অথবা) with the selected package's credits */}
                                {paidActions.map((item, i) => (
                                    <div key={item.module || i}>
                                        {i > 0 && (
                                            <div className="flex items-center gap-2 py-1">
                                                <div className="flex-1 h-px bg-zinc-100 dark:bg-zinc-800" />
                                                <span className="text-[13px] font-bold tracking-wide text-zinc-500 dark:text-zinc-400">{t('payments.orWord')}</span>
                                                <div className="flex-1 h-px bg-zinc-100 dark:bg-zinc-800" />
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2.5 py-0.5">
                                            <div className={`w-[26px] h-[26px] rounded-lg ${item.bg} ${item.color} flex items-center justify-center flex-shrink-0`}>{item.icon}</div>
                                            <span className="text-[13px] text-zinc-600 dark:text-zinc-300">{item.label}</span>
                                            <span className="ml-auto text-[15px] font-extrabold text-zinc-800 dark:text-zinc-100 tabular-nums">{fmtNum(item.count)} <span className="text-[11px] font-semibold text-zinc-400">{t('payments.countUnit')}</span></span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Coupon */}
                        <div className="px-5 pt-3.5 pb-1">
                            {!couponState ? (
                                <div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            placeholder={t('payments.couponPlaceholder')}
                                            value={couponCode}
                                            onChange={(e) => { setCouponCode(e.currentTarget.value.toUpperCase()); setCouponError(''); }}
                                            onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                                            className={`flex-1 px-3 py-2 border rounded-lg text-sm font-mono font-semibold tracking-wider bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                                                couponError ? "border-red-500" : "border-zinc-200 dark:border-zinc-700"
                                            }`}
                                        />
                                        <button
                                            disabled={!couponCode.trim() || validatingCoupon}
                                            onClick={handleApplyCoupon}
                                            className="px-4 py-2 rounded-lg text-sm font-semibold border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                        >
                                            {validatingCoupon
                                                ? <span className="inline-block w-3 h-3 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
                                                : t('payments.applyBtn')}
                                        </button>
                                    </div>
                                    {couponError && <p className="text-xs text-red-500 mt-1.5">{couponError}</p>}
                                </div>
                            ) : (
                                <div className={`flex items-center justify-between gap-2 p-2.5 rounded-lg border ${
                                    couponState.betterThanDefault
                                        ? "border-emerald-300 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-950/20"
                                        : "border-blue-300 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-950/20"
                                }`}>
                                    <div className="flex items-center gap-2 min-w-0">
                                        <IconTag size={14} className={couponState.betterThanDefault ? "text-emerald-600 flex-shrink-0" : "text-blue-600 flex-shrink-0"} />
                                        <div className="min-w-0">
                                            <span className="block text-xs font-bold font-mono tracking-wider text-zinc-800 dark:text-zinc-100">{couponCode}</span>
                                            {couponState.betterThanDefault
                                                ? <span className="text-xs font-semibold text-emerald-600">✓ {bnDigit(couponState.discountPercent)}% {t('payments.off')} — {curr}{fmtNum(couponState.saving)} {t('payments.saveWord')}</span>
                                                : <span className="text-xs text-blue-600">{couponState.message}</span>
                                            }
                                        </div>
                                    </div>
                                    <button onClick={handleRemoveCoupon} className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-600 flex-shrink-0">
                                        <IconX size={12} /> {t('payments.removeBtn')}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Price breakdown */}
                        <div className="px-5 py-3.5 border-t border-zinc-100 dark:border-zinc-800 mt-3">
                            <div className="flex items-center justify-between font-medium text-zinc-500 dark:text-zinc-400 mb-1.5">
                                <span className="text-lg">{t('payments.regularPrice')}</span>
                                <span className="text-lg line-through text-zinc-400">{curr}{fmtNum(basePrice)}</span>
                            </div>
                            {discountPercent > 0 && (
                                <div className="flex items-center justify-between font-medium text-emerald-600 dark:text-emerald-400 mb-1.5">
                                    <span className="text-lg">{t('payments.packageDiscount')} ({bnDigit(discountPercent)}%)</span>
                                    <span className="text-lg">− {curr}{fmtNum(savingAmount)}</span>
                                </div>
                            )}
                            {bonusCredits > 0 && (
                                <div className="flex items-center justify-between font-medium text-amber-600 dark:text-amber-400 mb-1.5">
                                    <span className="text-lg inline-flex items-center gap-1.5"><IconGift size={16} /> {t('payments.bonusCreditsLine')}</span>
                                    <span className="text-lg">+{fmtNum(bonusCredits)}</span>
                                </div>
                            )}
                            <div className="flex items-baseline justify-between border-t border-zinc-100 dark:border-zinc-800 pt-3 mt-1">
                                <span className="text-lg font-bold text-zinc-800 dark:text-zinc-100">{t('payments.totalPay')}</span>
                                <span className="text-4xl font-extrabold text-primary tabular-nums">{curr}{fmtNum(finalPrice)}</span>
                            </div>
                        </div>

                        {/* Floating checkout bar. Sticky: pinned to the bottom of the viewport while its natural
                            spot is below the fold, then settles into place as you scroll to it. It must be a DIRECT
                            child of the (tall) card — a sticky-bottom element only floats within its parent. Flat (no border/radius/card shadow) and compact
                            on the Y axis: terms + full-width pay in ~80px. Solid white bar; the ::before strip fades
                            the content out above it so nothing shows through behind the text. Below `nav` (1024px) it docks flush on the 52px
                            MobileBottomNav (PrivateLayout) — any gap there lets the card's rows scroll through the strip between bar and nav. */}
                        <div className="sticky z-20 bottom-[52px] nav:bottom-0 px-3 pt-2 pb-2 min-[1220px]:px-5 min-[1220px]:pt-3 min-[1220px]:pb-3 bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-700 before:content-[''] before:absolute before:inset-x-0 before:bottom-[calc(100%+1px)] before:h-7 before:bg-gradient-to-t before:from-white before:to-transparent dark:before:from-zinc-900 before:pointer-events-none">
                            <div className="space-y-2.5">
                                {/* Terms agreement — SSLCommerz tab only (Moneybag tab stays unchanged) */}
                                {gateway === 'sslcommerz' && (
                                    <div className="flex items-start gap-2">
                                        <label
                                            ref={termsLabelRef}
                                            onAnimationEnd={(e) => e.currentTarget.classList.remove('shake')}
                                            className="flex min-w-0 flex-1 items-start gap-3 py-1 cursor-pointer select-none"
                                        >
                                            <span className="relative inline-flex h-[22px] w-[22px] shrink-0">
                                                <input
                                                    ref={termsBoxRef}
                                                    type="checkbox"
                                                    checked={agreedToTerms}
                                                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                                                    className="peer h-full w-full appearance-none rounded-full border border-primary bg-zinc-100 dark:bg-zinc-800 hover:ring-2 hover:ring-primary/20 checked:bg-primary cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors"
                                                />
                                                {/* Hidden until agreed: the empty circle stays blank, the white check shows on the orange fill once ticked. */}
                                                <IconCheck size={15} strokeWidth={3.5} className="pointer-events-none absolute inset-0 m-auto text-white opacity-0 transition-opacity peer-checked:opacity-100" />
                                            </span>
                                            <span className="text-xs leading-[22px] text-zinc-600 dark:text-zinc-400">
                                                <Trans
                                                    i18nKey="payments.agreeTerms"
                                                    components={{
                                                        terms: <a href="https://sohozkaj.com/terms" target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline" />,
                                                        privacy: <a href="https://sohozkaj.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline" />,
                                                        refund: <a href="https://sohozkaj.com/refund-policy" target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline" />,
                                                    }}
                                                />
                                            </span>
                                        </label>
                                        {/* The how-to-pay video, at the end of the agreement line. Beside the label, not inside it: a button is not allowed in a label,
                                            and a click on it must never tick the box. mt-px centres the 28px button on the label's first line. */}
                                        <VideoHelpButton url={PAYMENT_GUIDE_VIDEO_URL} title={t("payments.guideVideoTitle")} className="mt-px" />
                                    </div>
                                )}
                                {/* sm+: one row while the pay button keeps ≥110px for its label. In a narrower summary column (≈768–980px viewports)
                                    the direct-pay buttons (bKash / Nagad) wrap under it instead of squeezing the label onto two lines. flex-grow works per line: pay's
                                    1000 vs the group's 1 → pay takes the spare room when they share a line, the group fills its own line once wrapped. */}
                                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                                    <button
                                        onClick={() => handlePurchase()}
                                        disabled={purchasing}
                                        className="w-full sm:w-auto sm:flex-[1000_1_110px] flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-primary hover:opacity-95 disabled:opacity-60 text-white font-bold text-sm sm:text-base rounded-xl py-2.5 transition-opacity disabled:cursor-not-allowed shadow-md shadow-primary/25"
                                    >
                                        {/* No icon on the label — only the spinner while the payment is being set up */}
                                        {purchasing && !purchasingChannel && (
                                            <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        )}
                                        {/* Below 1220px the button is too narrow for the amount (it is in the order summary anyway) — label only.
                                            global.scss sets `span { font-size: 12px }`, which beats the size inherited from the button, so the label size lives on the spans. */}
                                        <span className="text-base min-[1220px]:hidden">{t('payments.payNowShort')}</span>
                                        <span className="hidden text-base min-[1220px]:inline">{t('payments.payNowAmount', { amount: fmtNum(finalPrice), curr })}</span>
                                    </button>
                                    {gateway === 'sslcommerz' && directPay.length > 0 && (
                                        <div className={`grid ${directPay.length > 2 ? 'grid-cols-3' : 'grid-cols-2'} gap-2 sm:flex sm:grow sm:shrink-0`}>
                                            {directPay.map((c) => (
                                                <button
                                                    key={c.id}
                                                    type="button"
                                                    title={c.tip}
                                                    onClick={() => handlePurchase(c.id)}
                                                    disabled={purchasing}
                                                    className={`sm:grow flex items-center justify-center gap-1.5 bg-white dark:bg-zinc-100 border-2 ${c.cls} disabled:opacity-60 disabled:cursor-not-allowed text-zinc-700 font-bold text-sm rounded-xl px-3 h-[44px] transition-colors`}
                                                >
                                                    {purchasingChannel === c.id && purchasing
                                                        ? <span className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                                        : <img src={c.logo} alt="" className="h-8 w-8 object-contain" draggable={false} />}
                                                    {c.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="px-5 pb-5 -mt-1">
                            {/* bKash/Nagad/Card line is BD-specific — hide on the international (FastSpring) tab,
                                and on the SSLCommerz tab (its checkout page already lists the methods). */}
                            {gateway !== 'fastspring' && gateway !== 'sslcommerz' && (
                                <div className="flex items-center justify-center gap-1.5 mt-3 text-[11px] text-zinc-500 dark:text-zinc-400">
                                    <IconShieldCheck size={14} className="text-emerald-500" /> {t('payments.securePayment')}
                                </div>
                            )}
                            <p className="text-center text-[11px] leading-relaxed text-red-600 dark:text-red-400 mt-2">
                                {t('payments.paymentWaitNotice')}
                            </p>
                        </div>
                    </div>

                    {/* Post-payment steps */}
                    <div className="mt-3 border border-dashed border-zinc-300 dark:border-zinc-700 rounded-2xl p-4">
                        <p className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 mb-3">{t('payments.afterPayment')}</p>
                        <div className="flex gap-2.5">
                            {paymentSteps.map((s) => (
                                <div key={s.n} className="flex-1 flex flex-col gap-1.5">
                                    <div className="w-[26px] h-[26px] rounded-full bg-orange-100 dark:bg-orange-900/30 text-primary flex items-center justify-center font-bold text-xs">{bnDigit(s.n)}</div>
                                    <p className="text-xs font-semibold text-zinc-700 dark:text-zinc-200 leading-tight">{s.title}</p>
                                    <p className="text-[11px] text-zinc-500 dark:text-zinc-400 leading-snug">{s.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── How the discount works (full width) — only when the selected package
                actually has a discount; with no saving both bars show the same price. ── */}
            {savingAmount > 0 && (
            <div className="border border-zinc-200 dark:border-zinc-700 rounded-2xl p-5 bg-zinc-50 dark:bg-zinc-900 flex flex-col md:flex-row gap-5 md:gap-7 md:items-center">
                <div className="flex-1 min-w-0">
                    <h4 className="text-base sm:text-lg font-bold text-zinc-800 dark:text-zinc-100">{t('payments.howDiscountWorks')}</h4>
                    <p className="text-[13px] text-zinc-500 dark:text-zinc-400 mt-1.5 leading-relaxed">
                        {t('payments.discountIntro1')}<b className="text-zinc-800 dark:text-zinc-100">{t('payments.discountBold')}</b>{t('payments.discountIntro2', { rate: gateway === 'fastspring' ? '$0.01' : `৳${bnDigit(1)}` })}
                    </p>
                </div>
                <div className="flex-[1.1] min-w-0">
                    <div className="space-y-2">
                        <div className="flex items-center gap-2.5">
                            <span className="text-[11px] text-zinc-400 w-[80px] flex-shrink-0">{t('payments.regularPrice')}</span>
                            <div className="flex-1 h-3.5 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                            <span className="text-xs text-zinc-400 line-through w-12 text-right">{curr}{fmtNum(basePrice)}</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                            <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 w-[80px] flex-shrink-0">{t('payments.todayPrice')}</span>
                            <div className="flex-1 h-3.5 rounded-full bg-zinc-200 dark:bg-zinc-700 overflow-hidden">
                                <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600" style={{ width: `${basePrice ? Math.round((finalPrice / basePrice) * 100) : 0}%` }} />
                            </div>
                            <span className="text-[13px] font-bold text-emerald-700 dark:text-emerald-400 w-12 text-right">{curr}{fmtNum(finalPrice)}</span>
                        </div>
                    </div>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2.5">
                        {t('payments.exampleSaving', { credits: fmtNum(selected.credits), saving: fmtNum(savingAmount) })}
                    </p>
                </div>
            </div>
            )}

            {/* Accepted payment methods (cards + mobile banking) — this is the SSLCommerz/BD
                methods banner, so hide it on the international (FastSpring) tab. */}
            {gateway !== 'fastspring' && (
                <div className="pt-1">
                    <img
                        src="https://files.sohozkaj.com/SSLCommerz-Pay-With-logo.webp"
                        alt={t('payments.acceptedPaymentMethods')}
                        loading="lazy"
                        className="w-full h-auto"
                    />
                </div>
            )}
        </div>
    );
}
